import React, { useEffect, useState, useRef } from "react";
import { Col, Row, Card, CardText, Form, CardBody } from "reactstrap";
import Dropzone from "react-dropzone";
import SweetAlert from "react-bootstrap-sweetalert";
import readXlsxFile from "read-excel-file";
import { deleteSchedule } from "../../../utils/services/schedule";
import ModeloUpload from "../../../assets/statics/modelo_agenda_excluir.xlsx";
import { formatBytes } from "../../../utils/helpers/files/formatBytes";
import {
  allExcelColumnsDelete,
  excelColumnsDeletePtBr,
  getValidateAgenda,
} from "./importScheduleValidation";
import ModalExcelRows from "./ModalExcelRows";

// Schema estático — não precisa ser recriado a cada render
const DELETE_SCHEMA = {
  AgendaID: { prop: "id", type: String },
  Titulo: { prop: "title", type: String },
};

const schemaKeys = Object.keys(DELETE_SCHEMA).filter((key) => key !== "EventoID");

function isHeaderEqual(keys, header) {
  if (keys.length !== header.length) return false;
  return (
    keys.every((item) => header.includes(item)) &&
    header.every((item) => keys.includes(item))
  );
}

export default function ImportScheduleDelete(props) {
  const { isOpen, toggle, idEvent } = props;

  // state de arquivo selecionado
  const [selectedFiles, setSelectedFiles] = useState([]);

  // state de controle de fluxo
  const [successDlg, setSuccessDlg] = useState(false);
  const [importError, setImportError] = useState("");
  const [totalRows, setTotalRows] = useState(0);
  const [totalSended, setTotalSended] = useState(0);
  const [btnSend, setBtnSend] = useState(false);
  const [btnCancel, setBtnCancel] = useState(false);
  const importActivated = useRef(false);
  const timerID = useRef(null);

  // state do modal de erros no arquivo xlsx
  const [modalErrors, setModalErrors] = useState(false);
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    setSelectedFiles([]);
    setImportError("");
  }, [isOpen]);

  // Prepara preview e tamanho dos arquivos recebidos no dropzone
  function handleAcceptedFiles(files) {
    files.forEach((file) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
        formattedSize: formatBytes(file.size),
      }),
    );
    setSelectedFiles(files);
    setTotalRows(0);
    setTotalSended(0);
    setBtnSend(true);
    clearTimeout(timerID.current);
  }

  // Remove o arquivo selecionado antes do envio
  function removeSelectedFile() {
    if (selectedFiles[0]?.preview) URL.revokeObjectURL(selectedFiles[0].preview);
    setSelectedFiles([]);
    setBtnSend(false);
  }

  // Cancela o processo de importação em andamento
  function cancelImport() {
    importActivated.current = false;
    if (selectedFiles[0]?.preview) URL.revokeObjectURL(selectedFiles[0].preview);
    setSelectedFiles([]);
    setTotalRows(0);
    setTotalSended(0);
    setBtnSend(false);
    setBtnCancel(false);
  }

  async function uploadService() {
    importActivated.current = true;
    clearTimeout(timerID.current);
    setBtnSend(false);
    setBtnCancel(true);
    setImportError("");

    const { rows } = await readXlsxFile(selectedFiles[0], { schema: DELETE_SCHEMA });
    const validateRows = rows.map((row, i) => allExcelColumnsDelete(row, i));

    setTotalRows(validateRows.length);

    const rowsWithoutSchema = await readXlsxFile(selectedFiles[0]);
    const header = rowsWithoutSchema[0];

    if (!isHeaderEqual(schemaKeys, header)) {
      setErrors([]);
      setModalErrors(true);
      setBtnSend(true);
      setBtnCancel(false);
      return;
    }

    const validAgenda = await getValidateAgenda(validateRows, idEvent, "delete");
    if (validAgenda.length > 0) {
      setErrors(validAgenda);
      setModalErrors(true);
      setBtnSend(true);
      setBtnCancel(false);
      return;
    }

    let succeeded = 0;
    for (const row of validateRows) {
      if (!importActivated.current) break;

      const result = await deleteSchedule({ id: row.id }).catch(() => null);
      if (result?.message === "AGENDA_DISABLED") {
        succeeded++;
        setTotalSended(succeeded);
      }
    }

    if (importActivated.current) {
      setSuccessDlg(true);
    }
  }

  return (
    <div>
      {successDlg && (
        <SweetAlert
          success
          title="Upload"
          onConfirm={() => {
            toggle();
            setSuccessDlg(false);
          }}
        >
          O arquivo foi enviado com sucesso!
        </SweetAlert>
      )}
      {modalErrors && (
        <ModalExcelRows
          toggle={() => setModalErrors(false)}
          rows={errors}
          columnNames={excelColumnsDeletePtBr}
          typeImport="delete"
        />
      )}
      <div className="m-1 p-0">
        <Card className="p-1">
          <CardBody className="m-1 p-2">
            <CardText className="mb-2 p-10">
              O arquivo a ser importado deve seguir este{" "}
              <a href={ModeloUpload} download="modelo_agenda_excluir.xlsx">
                Modelo
              </a>
              , não modifique seu cabeçalho.
            </CardText>

            <Form>
              {selectedFiles.length === 0 && (
                <Dropzone
                  onDrop={(acceptedFiles) => handleAcceptedFiles(acceptedFiles)}
                >
                  {({ getRootProps, getInputProps }) => (
                    <div className="dropzone">
                      <div
                        className="dz-message needsclick mt-1"
                        {...getRootProps()}
                      >
                        <input {...getInputProps()} />
                        <div className="mb-4">
                          <i className="display-4 text-muted bx bxs-cloud-upload" />
                          <h4>Arraste o arquivo ou clique aqui e selecione.</h4>
                        </div>
                      </div>
                    </div>
                  )}
                </Dropzone>
              )}
              <div className="dropzone-previews mt-0" id="file-previews">
                {selectedFiles.map((f, i) => (
                  <Card
                    className="mt-1 mb-0 shadow-none border dz-processing dz-image-preview dz-success dz-complete"
                    key={i + "-file"}
                  >
                    <div className="p-1">
                      <Row className="align-items-center">
                        <Col xs="11">
                          <strong>{`Nome: ${f.name} - Tamanho: ${f.formattedSize}`}</strong>
                        </Col>
                        <Col xs="1">
                          <span
                            style={{
                              fontSize: "16px",
                              color: "red",
                              fontWeight: "700",
                            }}
                            className="mdi mdi-close"
                            onClick={removeSelectedFile}
                          />
                        </Col>
                      </Row>
                      {importActivated.current && (
                        <Row className="align-items-center">
                          <Col>
                            <strong>{`Total enviado: ${totalSended}/${totalRows}`}</strong>
                          </Col>
                        </Row>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            </Form>

            <div className="text-center mt-3">
              {importError && <p style={{ color: "red" }}>{importError}</p>}
              <button
                type="button"
                disabled={!btnSend}
                className="btn btn-primary waves-effect waves-light"
                onClick={uploadService}
              >
                Enviar Programação
              </button>
              <button
                type="button"
                className="btn btn-danger mx-1"
                disabled={!btnCancel}
                onClick={cancelImport}
              >
                Cancelar
              </button>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
